import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, Check } from 'lucide-react';
import { BuydownAnalytics } from '@/types/analytics';
import { formatCurrency } from '@/components/calculator/utils/formatters';
import { MetricTooltip } from './MetricTooltip';

interface BuydownAnalyticsCardProps {
  analytics: BuydownAnalytics;
}

export const BuydownAnalyticsCard: React.FC<BuydownAnalyticsCardProps> = ({ analytics }) => {
  const maxValueScore = Math.max(...analytics.scenarios.map(s => s.valueScore));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
           <TrendingUp className="w-5 h-5" />
          Buydown Scenario Comparison
          <MetricTooltip metric="buydownScenarios" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {analytics.scenarios.map(scenario => (
              <div 
                key={scenario.scenarioType} 
                className={`text-center p-4 rounded-lg border-2 transition-all ${
                  scenario.isRecommended 
                    ? 'bg-primary/5 border-primary shadow-md' 
                    : 'bg-muted border-border'
                }`}
              >
                {scenario.isRecommended && (
                  <Badge className="mb-2" variant="default">
                    <Check className="w-3 h-3 mr-1" />
                    BEST VALUE
                  </Badge>
                )}
                <p className="font-semibold text-lg">{scenario.scenarioType.toUpperCase()}</p>
                <p className="text-sm text-muted-foreground mt-1">Net Benefit</p>
                 <p className="text-xl font-bold text-green-600">{formatCurrency(scenario.netBenefit)}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Break-even: {scenario.breakEvenMonths} months <MetricTooltip metric="breakEvenMonths" iconSize={12} />
                </p>
                <Progress 
                  value={(scenario.valueScore / maxValueScore) * 100} 
                  className="h-2 mt-2" 
                />
              </div>
            ))}
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
            <h4 className="font-semibold mb-2 text-blue-900 dark:text-blue-100">Optimal Strategy</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">{analytics.optimalStrategy}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Savings Range</p>
              <p className="text-lg font-semibold">
                {formatCurrency(analytics.totalSavingsRange.min)} - {formatCurrency(analytics.totalSavingsRange.max)}
              </p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Recommended</p>
              <p className="text-lg font-semibold text-primary">
                {analytics.bestScenario.toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
